import React, { useState, Fragment, useCallback, useEffect } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import PageHead from "../../components/pageHead";
import CheckListCard from "../../components/checkListCard";
import LoadingButton from "../../components/loadingButton";
import Button from "../../components/button";
import SelectInput from "../../components/selectInput";
import TutorialChecklistImage from "../../assets/images/tutorialChecklist.png";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import MobileNav from "../../components/mobileNav";
import styles from "./style.module.css";
import formStyles from "../../components/form/style.module.css";

const schema = {
  type: "object",
  required: ["task"],
  properties: {
    task: {
      title: "Item",
      type: "string",
    },
  },
};

const uiSchema = {
  "ui:classNames": formStyles.formInner_p0,
  "ui:rootFieldId": "add_checklist",
  task: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_auto}`,
    "ui:placeholder": "Enter Item",
  },
  "ui:submitButtonOptions": {
    props: {
      className: formStyles.form_btn,
    },
  },
};

const options = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "incomplete", label: "Incomplete" },
];

const Checklist = () => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isMobile = useMediaQuery("(max-width: 768px)");
  const axiosPrivate = useAxiosPrivate();
  const [taskData, setTaskData] = useState([]);
  const [completedTask, setCompletedTask] = useState([]);
  const [totalTask, setTotalTask] = useState(0);
  const [formData, setFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(options[0]);

  const fetchChecklist = useCallback(async () => {
    try {
      let url = `checklists/items`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            let data = response.data.checklists;
            let tempTask = data.filter((item) => item.status === false);
            setTaskData(tempTask);
            setCompletedTask(data.filter((item) => item.status === true));
            setTotalTask(response.data.total);
          }
        })
        .catch((err) => {
          if (err.response) {
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      // console.error('error', error);
    }
    // eslint-disable-next-line
  }, []);

  const handleAddChecklist = useCallback(
    async (data) => {
      setSaveLoading(true);
      try {
        let url = `checklists/items`;
        let options = {
          method: "POST",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              setFormData({});
              setTaskData((prevData) => [response.data.checklist, ...prevData]);
              setTotalTask((prevTotal) => prevTotal + 1);
            }
          })
          .catch((err) => {
            console.error("Error adding checklist:", err);
          })
          .finally(() => {
            setSaveLoading(false);
          });
      } catch (error) {
        console.error("Error adding checklist:", error);
        setSaveLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onFormSubmit = (formData) => {
    setFormData(formData);
    handleAddChecklist({ title: formData.task });
  };

  const handleUpdateChecklist = useCallback(
    async (data, id) => {
      setSaveLoading(true);
      try {
        let url = `checklists/items/${id}`;
        let options = {
          method: "PUT",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              setSaveLoading(false);
              setTaskData((prevData) =>
                prevData.map((item) =>
                  item._id === id ? { ...item, title: data.title } : item
                )
              );
              setCompletedTask((prevData) =>
                prevData.map((item) =>
                  item._id === id ? { ...item, title: data.title } : item
                )
              );
            }
            setSaveLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              setSaveLoading(false);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
    }, // eslint-disable-next-line
    []
  );

  const onTaskEdit = (id, title) => {
    handleUpdateChecklist({ title: title }, id);
  };

  const handleUpdateStatus = useCallback(
    async (data, id) => {
      setSaveLoading(true);
      try {
        let url = `checklists/items/${id}/status`;
        let options = {
          method: "PUT",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              setSaveLoading(false);

              if (data.status) {
                // Find task in taskData and move to completedTask
                const taskToMove = taskData.find((item) => item._id === id);
                if (taskToMove) {
                  setCompletedTask((prevCompleted) => [
                    ...prevCompleted,
                    { ...taskToMove, status: true },
                  ]);
                  setTaskData((prevTasks) =>
                    prevTasks.filter((item) => item._id !== id)
                  );
                }
              } else {
                // Find task in completedTask and move to taskData
                const taskToMove = completedTask.find(
                  (item) => item._id === id
                );
                if (taskToMove) {
                  setTaskData((prevTasks) => [
                    { ...taskToMove, status: false },
                    ...prevTasks,
                  ]);
                  setCompletedTask((prevCompleted) =>
                    prevCompleted.filter((item) => item._id !== id)
                  );
                }
              }
            }
            setSaveLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              setSaveLoading(false);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskData]
  );

  const onTaskStatusChange = (status, id) => {
    handleUpdateStatus({ status: status }, id);
  };

  const onTaskRemove = async (id) => {
    setSaveLoading(true);
    try {
      const response = await axiosPrivate.delete(`/checklists/items/${id}`);
      if (response.data.status === 1) {
        setTaskData((prevData) => prevData.filter((item) => item._id !== id));
        setCompletedTask((prevData) =>
          prevData.filter((item) => item._id !== id)
        );
        setTotalTask((prevTotal) => prevTotal - 1);
        setSaveLoading(false);
      }
    } catch (error) {
      setSaveLoading(false);
    }
  };

  const handleShowCompleted = (value) => {
    setShowCompleted(value);
  };

  const handleUpdateOrder = useCallback(
    async (data, id, oldIndex, newIndex) => {
      setSaveLoading(true);
      try {
        let url = `checklists/items/${id}/order`;
        let options = {
          method: "PUT",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 0) {
              setTaskData((prevData) =>
                arrayMove(prevData, newIndex, oldIndex)
              );
            }
            setSaveLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              if (err.response.data.status === 0) {
                setTaskData((prevData) =>
                  arrayMove(prevData, newIndex, oldIndex)
                );
              }
              setSaveLoading(false);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = taskData.findIndex((item) => item._id === active.id);
      const newIndex = taskData.findIndex((item) => item._id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        handleUpdateOrder({ order: newIndex }, active.id, oldIndex, newIndex);
        setTaskData((prevData) => arrayMove(prevData, oldIndex, newIndex));
      }
    }
  };

  const handleUncheckAll = useCallback(async () => {
    setSaveLoading(true);
    try {
      let url = `checklists/items/reset`;
      let options = {
        method: "PUT",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            fetchChecklist();
          }
          setSaveLoading(false);
        })
        .catch((err) => {
          if (err.response) {
            setSaveLoading(false);
          }
        });
    } catch (error) {
      setSaveLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  return (
    <Fragment>
      <PageHead
        title="Checklist"
        logo={isMobile}
        tutorialContent={
          <Fragment>
            <p>
              The <strong>Checklist Page</strong> helps you pack essential items
              before a track day. This list can include:
            </p>
            <ul>
              <li>
                <strong>Car-related gear:</strong> e.g., torque wrench, tire
                pressure gauge, tools, jack
              </li>
              <li>
                <strong>Personal items:</strong> e.g., helmet, gloves,
                sunglasses, tech form, chair, cooler, snacks, etc.
              </li>
            </ul>
            <p>
              Once your checklist is created, simply check off each item as you
              pack. You can:
            </p>
            <ul>
              <li>
                <strong>Sort</strong> items by checked/unchecked status
              </li>
              <li>
                <strong>Reset</strong> the checklist before each new track event
              </li>
            </ul>
            <p>
              This ensures you never forget an important item before hitting the
              track!
            </p>
            <img src={TutorialChecklistImage} alt={"Checklist Page"} />
          </Fragment>
        }
      />
      <div className={styles.body}>
        <div className={styles.body_head}>
          <div className={styles.add_form}>
            <Form
              className={`${formStyles.form} ${formStyles.form_inline}`}
              schema={schema}
              uiSchema={uiSchema}
              formData={formData}
              validator={validator}
              showErrorList={false}
              omitExtraData={true}
              liveValidate={false}
              onSubmit={({ formData }) => {
                onFormSubmit(formData);
              }}
              disabled={saveLoading}
            >
              <div className={styles.form_actions}>
                <LoadingButton
                  type="submit"
                  loading={saveLoading}
                  title={isMobile ? "Add" : "Add Item"}
                />
              </div>
            </Form>
            <div className={styles.filter_actions}>
              <SelectInput
                options={{
                  options: options,
                  customClass: styles.filter_select,
                }}
                value={JSON.stringify(showCompleted)}
                isFormField={false}
                placeholder="Select Item"
                onChange={handleShowCompleted}
              />
              <Button
                title="Reset"
                onClick={handleUncheckAll}
                className={styles.filter_btn}
                disabled={completedTask.length === 0}
              />
            </div>
          </div>
        </div>
        <div className={styles.body_view}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={taskData.map((item) => item._id)}
              strategy={verticalListSortingStrategy}
            >
              {taskData.map((item) => (
                <CheckListCard
                  key={item._id}
                  item={item}
                  onStatusChange={onTaskStatusChange}
                  onRemove={onTaskRemove}
                  totalTask={totalTask}
                  showCompleted={showCompleted}
                  onEdit={onTaskEdit}
                  saveLoading={saveLoading}
                />
              ))}
            </SortableContext>
          </DndContext>
          {completedTask.map((item) => (
            <CheckListCard
              key={item._id}
              item={item}
              onStatusChange={onTaskStatusChange}
              onRemove={onTaskRemove}
              totalTask={totalTask}
              showCompleted={showCompleted}
              onEdit={onTaskEdit}
              saveLoading={saveLoading}
            />
          ))}
        </div>
      </div>
      {isMobile && <MobileNav defaultActive={true} isScrolling={false} />}
    </Fragment>
  );
};

export default Checklist;
